import { isEqual } from 'lodash'
import { z } from 'zod'
import { LocalStorage } from '@library/enums/local-storage'
import { joinText } from '@library/utils/functions'
import { isType } from '@library/utils/functions/is-type'
import { useEnv } from './use-env'
import {
  useStorage as useVueUseStorage,
  type UseStorageOptions
} from '@vueuse/core'
import { toValue, type MaybeRefOrGetter } from 'vue'

/** localStorage 存取工具 */
export function useStorage() {
  const { VITE_STORAGE_PREFIX_KEY } = useEnv()

  /** 取得響應式 localStorage 資料 */
  const getStorageReactiveData = <T extends z.ZodType>(option: {
    key: LocalStorage
    defaultData: MaybeRefOrGetter<z.infer<T>>
    storage?: Storage
    zodSchema: T
    options?: UseStorageOptions<z.infer<T>>
  }) => {
    const {
      defaultData,
      key,
      storage = localStorage,
      options,
      zodSchema
    } = option

    const storageData = useVueUseStorage<z.infer<T>>(
      getStorageKey(key),
      defaultData,
      storage,
      options
    )

    // 檢查資料是否符合 zodSchema，不符合則設定成預設值
    if (!isType(storageData.value, zodSchema)) {
      storageData.value = toValue(defaultData)
    }

    return storageData
  }

  /** 取得 localStorage 資料 */
  const getStorageData = <T extends z.ZodType>(
    option:
      | LocalStorage
      | {
          key: LocalStorage
          zodSchema: T
          autoRemove?: boolean
        }
  ): z.infer<T> | undefined => {
    const key = typeof option === 'string' ? option : option.key
    const zodSchema = typeof option === 'string' ? z.any() : option.zodSchema
    const autoRemove =
      typeof option === 'string' ? false : (option.autoRemove ?? false)

    const storageData = localStorage.getItem(getStorageKey(key))

    if (autoRemove) {
      removeStorageData(key)
    }

    if (!storageData) {
      return undefined
    }

    const data = parseStorageData(storageData)

    if (isType(data, zodSchema, true)) {
      return data
    } else {
      removeStorageData(key)
      return undefined
    }
  }

  const parseStorageData = (value: string): unknown => {
    try {
      const parseData = JSON.parse(value)

      return typeof parseData === 'object' ? parseData : value
    } catch {
      return value
    }
  }

  /** 設定 localStorage 資料 */
  const setStorageData = (key: LocalStorage, value: any) => {
    const storageData = getStorageData(key)

    if (isEqual(storageData, value)) {
      return
    }

    localStorage.setItem(
      getStorageKey(key),
      typeof value === 'object' ? JSON.stringify(value) : value
    )
  }

  /** 移除 localStorage 資料 */
  const removeStorageData = (key: LocalStorage) => {
    localStorage.removeItem(getStorageKey(key))
  }

  /** 取得含前綴的 storage key */
  const getStorageKey = (key: string): string => {
    return `${joinText([VITE_STORAGE_PREFIX_KEY, key], '__')}__`
  }

  return {
    /** 取得響應式 localStorage 資料 */
    getStorageReactiveData,
    /** 取得 localStorage 資料 */
    getStorageData,
    /** 設定 localStorage 資料 */
    setStorageData,
    /** 移除 localStorage 資料 */
    removeStorageData,
    /** 取得含前綴的 storage key */
    getStorageKey
  }
}
